import { HayvenMarkLoader, useLoaderRevealClass } from '@/components/brand/hayven-mark-loader'
import { cn } from '@/lib/utils'

/**
 * Compact spinning "bloom" for spots that already show the Hayven lockup (login, SSO), where a
 * second logo would be noise. Whole-screen boot uses `PageLoader`; in-shell loads use skeletons.
 *
 * Eight soft, rounded brand petals form a ring with a fading comet tail
 * (bright leading petal → faint trailing petals) that rotates continuously.
 *
 * Rotation is gated behind `motion-safe:` so reduced-motion users see a
 * gentle static bloom instead of an animation.
 */

const PETAL_COUNT = 8

interface ClovieSpinnerProps {
  /** Pixel size of the square mark. Defaults to 32. */
  size?: number
  className?: string
}

export function ClovieSpinner({ size = 32, className }: ClovieSpinnerProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      role="img"
      aria-hidden="true"
      className={cn(
        'text-clovie-brand motion-safe:animate-spin [animation-duration:1.1s]',
        className
      )}
    >
      {Array.from({ length: PETAL_COUNT }).map((_, i) => (
        <rect
          key={i}
          x="10.85"
          y="2"
          width="2.3"
          height="6.4"
          rx="1.15"
          fill="currentColor"
          // Comet tail: leading petal full, trailing petals fade out.
          fillOpacity={1 - (i / PETAL_COUNT) * 0.78}
          transform={`rotate(${i * (360 / PETAL_COUNT)} 12 12)`}
        />
      ))}
    </svg>
  )
}

interface PageLoaderProps {
  /** Optional text shown under the mark. Omit for a bare mark. */
  label?: string
  /** Pixel height of the Hayven mark. Defaults to 44. */
  size?: number
  className?: string
}

/**
 * Whole-viewport branded boot loader: the animated Hayven mark centered on a blank canvas, plus an
 * optional label that appears together with it after the 200ms reveal delay.
 *
 * Only for pre-shell moments (app boot, org resolve, workspace access) when nothing else can be
 * drawn yet. Once the app shell (sidebar rail) is on screen, pages, panels and lists use skeletons
 * shaped like their content (`components/ui/skeleton.tsx`) — a second, re-centered logo inside the
 * shell reads as the loader jumping.
 */
export function PageLoader({ label, size = 44, className }: PageLoaderProps) {
  const labelRevealClass = useLoaderRevealClass()
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn('flex min-h-screen w-full items-center justify-center bg-clovie-canvas', className)}
    >
      <div className="flex flex-col items-center gap-4">
        <HayvenMarkLoader size={size} />
        {label ? (
          <p className={cn('text-base font-medium text-clovie-muted', labelRevealClass)}>{label}</p>
        ) : null}
        <span className="sr-only">Loading</span>
      </div>
    </div>
  )
}
